import { useEffect, useState } from "react";
import { TextField, SelectField } from "@/components/base/FormControls";
import ImageUploader from "@/components/base/ImageUploader";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useAuth } from "@/context/AuthContext";
import { useAppSettings } from "@/context/AppSettingsContext";
import { useToast } from "@/context/ToastContext";

interface CompanyForm {
  company_name: string;
  rnc: string;
  address: string;
  phone: string;
  whatsapp: string;
  email: string;
  logo_url: string;
  favicon_url: string;
  theme: string;
  facebook: string;
  instagram: string;
  tiktok: string;
  youtube: string;
  other: string;
}

const EMPTY: CompanyForm = {
  company_name: "",
  rnc: "",
  address: "",
  phone: "",
  whatsapp: "",
  email: "",
  logo_url: "",
  favicon_url: "",
  theme: "light",
  facebook: "",
  instagram: "",
  tiktok: "",
  youtube: "",
  other: "",
};

export default function CompanyTab() {
  const toast = useToast();
  const { profile } = useAuth();
  const { settings, social, settingsId, refresh } = useAppSettings();
  const [form, setForm] = useState<CompanyForm>(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      company_name: settings.company_name ?? "",
      rnc: settings.rnc ?? "",
      address: settings.address ?? "",
      phone: settings.phone ?? "",
      whatsapp: settings.whatsapp ?? "",
      email: settings.email ?? "",
      logo_url: settings.logo_url ?? "",
      favicon_url: settings.favicon_url ?? "",
      theme: settings.theme ?? "light",
      facebook: social.facebook ?? "",
      instagram: social.instagram ?? "",
      tiktok: social.tiktok ?? "",
      youtube: social.youtube ?? "",
      other: social.other ?? "",
    });
  }, [settings, social]);

  const update = <K extends keyof CompanyForm>(key: K, value: CompanyForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!form.company_name.trim()) {
      toast.error("El nombre de la empresa es obligatorio.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        company_name: form.company_name.trim(),
        rnc: form.rnc.trim() || null,
        address: form.address.trim() || null,
        phone: form.phone.trim() || null,
        whatsapp: form.whatsapp.trim() || null,
        email: form.email.trim() || null,
        logo_url: form.logo_url || null,
        favicon_url: form.favicon_url || null,
        theme: form.theme,
        social: {
          facebook: form.facebook.trim() || undefined,
          instagram: form.instagram.trim() || undefined,
          tiktok: form.tiktok.trim() || undefined,
          youtube: form.youtube.trim() || undefined,
          whatsapp: form.whatsapp.trim() || undefined,
          other: form.other.trim() || undefined,
        },
        updated_at: new Date().toISOString(),
      };

      const query = settingsId
        ? supabase.from("app_settings").update(payload).eq("id", settingsId)
        : supabase.from("app_settings").insert(payload);
      const { error } = await query;
      if (error) {
        toast.error("No se pudo guardar la configuración.");
        return;
      }

      await logAudit({
        userId: profile?.id ?? null,
        userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
        action: "configurar",
        entity: "app_settings",
        entityId: settingsId ?? "app_settings",
        newValue: { company_name: payload.company_name, theme: payload.theme },
      });

      await refresh();
      toast.success("Configuración de empresa guardada.");
    } catch {
      toast.error("Ocurrió un error inesperado.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-5 rounded-lg border border-background-200 bg-background-100 p-5">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-building-line text-base"></i>
          </span>
          <h3 className="font-heading text-sm font-semibold text-foreground-900">Datos de la empresa</h3>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="Nombre de la empresa"
            name="company_name"
            value={form.company_name}
            onChange={(value) => update("company_name", value)}
            placeholder="Sistema RKC"
            required
          />
          <TextField
            label="RNC"
            name="rnc"
            value={form.rnc}
            onChange={(value) => update("rnc", value)}
            placeholder="1-01-00000-1"
          />
          <TextField
            label="Teléfono"
            name="phone"
            value={form.phone}
            onChange={(value) => update("phone", value)}
            inputMode="tel"
            placeholder="809-000-0000"
          />
          <TextField
            label="WhatsApp"
            name="whatsapp"
            value={form.whatsapp}
            onChange={(value) => update("whatsapp", value)}
            inputMode="tel"
            placeholder="809-000-0000"
          />
          <TextField
            label="Correo"
            name="email"
            type="email"
            value={form.email}
            onChange={(value) => update("email", value)}
            inputMode="email"
            placeholder="contacto@rkc.com"
          />
          <TextField
            label="Dirección"
            name="address"
            value={form.address}
            onChange={(value) => update("address", value)}
            placeholder="Av. Principal #123, Ciudad"
          />
        </div>
      </section>

      <section className="flex flex-col gap-5 rounded-lg border border-background-200 bg-background-100 p-5">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-800">
            <i className="ri-image-line text-base"></i>
          </span>
          <h3 className="font-heading text-sm font-semibold text-foreground-900">Apariencia</h3>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <ImageUploader
            label="Logo"
            value={form.logo_url || null}
            onChange={(url) => update("logo_url", url)}
            folder="logo"
            hint="PNG o WebP con fondo transparente, recomendado 512x512."
            previewClassName="h-20 w-20"
          />
          <ImageUploader
            label="Favicon"
            value={form.favicon_url || null}
            onChange={(url) => update("favicon_url", url)}
            folder="favicon"
            hint="Icono de la pestaña del navegador. Recomendado 64x64."
            previewClassName="h-14 w-14"
          />
        </div>
        <div className="sm:max-w-xs">
          <SelectField
            label="Tema"
            name="theme"
            value={form.theme}
            onChange={(value) => update("theme", value)}
            options={[
              { value: "light", label: "Claro" },
              { value: "dark", label: "Oscuro" },
            ]}
          />
        </div>
      </section>

      <section className="flex flex-col gap-5 rounded-lg border border-background-200 bg-background-100 p-5">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-200 text-secondary-900">
            <i className="ri-share-line text-base"></i>
          </span>
          <h3 className="font-heading text-sm font-semibold text-foreground-900">Redes sociales</h3>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="Facebook"
            name="facebook"
            value={form.facebook}
            onChange={(value) => update("facebook", value)}
            placeholder="https://facebook.com/rkcmotor"
          />
          <TextField
            label="Instagram"
            name="instagram"
            value={form.instagram}
            onChange={(value) => update("instagram", value)}
            placeholder="https://instagram.com/rkcmotor"
          />
          <TextField
            label="TikTok"
            name="tiktok"
            value={form.tiktok}
            onChange={(value) => update("tiktok", value)}
            placeholder="https://tiktok.com/@rkcmotor"
          />
          <TextField
            label="YouTube"
            name="youtube"
            value={form.youtube}
            onChange={(value) => update("youtube", value)}
            placeholder="https://youtube.com/@rkcmotor"
          />
          <TextField
            label="Otra red"
            name="other"
            value={form.other}
            onChange={(value) => update("other", value)}
            placeholder="https://..."
          />
        </div>
      </section>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
        >
          {saving ? (
            <i className="ri-loader-4-line animate-spin text-base"></i>
          ) : (
            <i className="ri-save-3-line text-base"></i>
          )}
          Guardar cambios
        </button>
      </div>
    </div>
  );
}